/**
 * Деньги в SKOKA — целое число копеек.
 *
 * Почему не bigint, хотя в PRD §13 написано bigint: в TypeScript целое `number`
 * точен до 2^53 копеек ≈ 90 триллионов рублей, чего заведомо хватает, зато он
 * сериализуется в JSON, работает в арифметике без приведений и не требует
 * bigint-совместимости во всём стеке. В Postgres колонка остаётся `bigint`.
 *
 * Единственное правило, которое нельзя нарушать: **ни одного дробного значения
 * в денежных путях**. Любое деление проходит через округление, любое
 * распределение — через `distribute`, который не теряет копейки.
 */

declare const MoneyBrand: unique symbol;
export type Money = number & { readonly [MoneyBrand]: true };

export const ZERO = 0 as Money;

/** Копейки → Money. Бросает на дробном значении: это всегда ошибка расчёта. */
export function kop(n: number): Money {
  if (!Number.isFinite(n)) throw new TypeError(`Money: не число (${n})`);
  if (!Number.isInteger(n)) throw new TypeError(`Money: дробные копейки (${n})`);
  if (!Number.isSafeInteger(n)) throw new TypeError(`Money: выход за безопасный диапазон (${n})`);
  return n as Money;
}

/** Рубли → Money. Банковское округление до копейки. */
export function rub(n: number): Money {
  return kop(roundHalfEven(n * 100));
}

/**
 * Банковское округление (half to even). Обычный Math.round смещает суммы вверх:
 * на десятках тысяч операций это накопленная ошибка в пользу или против
 * пользователя, чего в финансовом приложении быть не должно.
 */
export function roundHalfEven(x: number): number {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (diff > 0.5) return floor + 1;
  if (diff < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

export const add = (...xs: Money[]): Money => kop(xs.reduce((a, b) => a + b, 0));
export const sub = (a: Money, b: Money): Money => kop(a - b);
export const neg = (a: Money): Money => kop(-a);
export const abs = (a: Money): Money => kop(Math.abs(a));
export const isNeg = (a: Money): boolean => a < 0;
export const isZero = (a: Money): boolean => a === 0;
export const maxM = (a: Money, b: Money): Money => (a >= b ? a : b);
export const minM = (a: Money, b: Money): Money => (a <= b ? a : b);
/** Отсечение снизу нулём: «сколько ещё не хватает», «сколько осталось». */
export const clampPos = (a: Money): Money => (a > 0 ? a : ZERO);

/** Умножение на количество (весовой товар, N штук). Банковское округление. */
export function mulQty(price: Money, qty: number): Money {
  if (!Number.isFinite(qty) || qty < 0) throw new TypeError(`Money: некорректное количество (${qty})`);
  return kop(roundHalfEven(price * qty));
}

/** Доля от суммы: 0.2 → 20%. */
export function share(amount: Money, fraction: number): Money {
  if (!Number.isFinite(fraction)) throw new TypeError(`Money: некорректная доля (${fraction})`);
  return kop(roundHalfEven(amount * fraction));
}

/**
 * Деление суммы по весам без потери копеек (метод наибольшего остатка).
 * Сумма результата **всегда** равна исходной — это то, на чём держится
 * контрольная проверка чека и распределение дохода по целям.
 */
export function distribute(total: Money, weights: number[]): Money[] {
  if (weights.length === 0) return [];
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum <= 0) {
    // веса пустые — отдаём всё первому, чтобы копейки не исчезли
    return weights.map((_, i) => (i === 0 ? total : ZERO));
  }
  const exact = weights.map((w) => (total * w) / sum);
  const floors = exact.map((x) => Math.floor(x));
  let rest = total - floors.reduce((a, b) => a + b, 0);
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac);
  const out = floors.slice();
  for (const { i } of order) {
    if (rest === 0) break;
    const step = rest > 0 ? 1 : -1;
    out[i] = (out[i] ?? 0) + step;
    rest -= step;
  }
  return out.map((n) => kop(n));
}

/** Деление на равные части в день/месяц: округляем вниз, чтобы не пообещать лишнего. */
export function perUnitFloor(amount: Money, units: number, stepKop = 1): Money {
  if (!Number.isInteger(units) || units <= 0) throw new TypeError(`Money: units (${units})`);
  const raw = Math.floor(amount / units);
  return kop(Math.floor(raw / stepKop) * stepKop);
}

const NBSP = ' ';
const MINUS = '−'; // настоящий минус, не дефис: в табличных цифрах дефис короче

export interface FormatOptions {
  /** Показывать копейки. По умолчанию — только если они не нулевые. */
  kopecks?: 'auto' | 'always' | 'never';
  /** '+' у положительных, '−' у отрицательных. */
  sign?: boolean;
  /** Символ валюты. */
  currency?: string | null;
}

export function formatMoney(m: Money, o: FormatOptions = {}): string {
  const { kopecks = 'auto', sign = false, currency = '₽' } = o;
  const negative = m < 0;
  const v = Math.abs(m);
  const whole = Math.floor(v / 100);
  const cents = v % 100;
  const showCents = kopecks === 'always' || (kopecks === 'auto' && cents !== 0);

  let s = whole.toLocaleString('ru-RU').replace(/\s/g, NBSP);
  if (showCents) s += ',' + String(cents).padStart(2, '0');
  if (currency) s += NBSP + currency;
  if (negative) s = MINUS + NBSP + s;
  else if (sign && m > 0) s = '+' + NBSP + s;
  return s;
}

/** Разбор строки из чека или ввода: «1 190.00», «6 840,00», «−450». */
export function parseMoney(input: string): Money {
  const cleaned = input
    .replace(/[\s ]/g, '')
    .replace(/[−–—]/g, '-')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  if (cleaned === '' || cleaned === '-') throw new TypeError(`Money: не разобрал «${input}»`);
  const n = Number(cleaned);
  if (!Number.isFinite(n)) throw new TypeError(`Money: не разобрал «${input}»`);
  return rub(n);
}
